import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Rendered when a descendant throws during render or a lifecycle method.
   * Receives the error and a `reset` callback.
   */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** Optional hook for logging / reporting. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-phase errors from its subtree (issue #216).
 *
 * The motivating case is a failed `React.lazy` dynamic import — e.g. the
 * Runbook editor chunk while offline — which would otherwise leave the route
 * blank with no explanation.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    return error ? this.props.fallback(error, this.reset) : this.props.children;
  }
}
